import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Paper, Box } from "@mui/material";

import useOrderDashboard from "../../hooks/useOrderDashboard";
import Loading from "../ui/Loading";
import Page from "../pages/Page";

import OrderDashboardFilters from "./OrderDashboardFilters";
import OrderMetricsCards from "./OrderMetricsCards";
import OrderVolumeChart from "./OrderVolumeChart";
import OrdersByRepTable from "./OrdersByRepTable";
import OrdersByLocationTable from "./OrdersByLocationTable";
import TopRepsTable from "./TopRepsTable";
import RepOrderTrendChart from "./RepOrderTrendChart";
import TopSkusTable from "./TopSkusTable";
import SkuCategoryChart from "./SkuCategoryChart";
import TrendingSkusChart from "./TrendingSkusChart";
import OrderCompositionCard from "./OrderCompositionCard";

const OrderDashboard = () => {
  const navigate = useNavigate();
  const {
    filters,
    updateFilters,
    metrics,
    orderVolume,
    ordersByRep,
    ordersByLocation,
    topReps,
    repOrderTrends,
    topSkus,
    skuCategories,
    trendingSkus,
    orderComposition,
    isLoading,
    getMetrics,
    getOrderVolume,
    getOrdersByRep,
    getOrdersByLocation,
    getTopReps,
    getRepOrderTrends,
    getTopSkus,
    getSkuCategories,
    getTrendingSkus,
    getOrderComposition,
  } = useOrderDashboard();

  // Initial load
  useEffect(() => {
    getMetrics();
    getOrderVolume();
    getOrdersByRep();
    getOrdersByLocation();
    getTopReps();
    getRepOrderTrends();
    getTopSkus();
    getSkuCategories();
    getTrendingSkus();
    getOrderComposition();
  }, []);

  const handleFilterChange = (newFilters) => {
    updateFilters(newFilters);
    // Re-fetch all data with new filters
    getMetrics();
    getOrderVolume();
    getOrdersByRep();
    getOrdersByLocation();
    getTopReps();
    getRepOrderTrends();
    getTopSkus();
    getSkuCategories();
    getTrendingSkus();
    getOrderComposition();
  };

  const handleToolBarClick = (action) => {
    switch (action) {
      case "syncDashboard":
        navigate("/syncdashboard");
        break;
      case "callDashboard":
        navigate("/home");
        break;
      case "surveyDashboard":
        navigate("/surveymetrics");
        break;
      case "clientDashboard":
        navigate("/clientdashboard");
        break;
      case "repDashboard":
        navigate("/repdashboard");
        break;
      default:
        break;
    }
  };

  if (isLoading && !metrics.data) {
    return <Loading />;
  }

  return (
    <Page
      pageName="orderDashboard"
      includeToolbar={true}
      onToolItemClick={handleToolBarClick}
      items={[
        { name: "syncDashboard", iconName: "CloudSync" },
        { name: "callDashboard", iconName: "CalendarMonth" },
        { name: "surveyDashboard", iconName: "Assignment" },
        { name: "clientDashboard", iconName: "Business" },
        { name: "repDashboard", iconName: "People" },
      ]}
    >
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <OrderDashboardFilters
            filters={filters}
            onChange={handleFilterChange}
          />
        </Paper>

        <Grid
          container
          spacing={3}
        >
          {/* Key Metrics Row */}
          <Grid size={{ xs: 12 }}>
            <OrderMetricsCards
              data={metrics.data}
              isLoading={metrics.isLoading}
              error={metrics.error}
            />
          </Grid>

          {/* Order Volume & Performance Section */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <OrderVolumeChart
                  data={orderVolume.data}
                  isLoading={orderVolume.isLoading}
                  error={orderVolume.error}
                />
              </Box>

              <Grid
                container
                spacing={2}
              >
                <Grid size={{ xs: 12, md: 6 }}>
                  <OrdersByRepTable
                    data={ordersByRep.data}
                    isLoading={ordersByRep.isLoading}
                    error={ordersByRep.error}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <OrdersByLocationTable
                    data={ordersByLocation.data}
                    isLoading={ordersByLocation.isLoading}
                    error={ordersByLocation.error}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Rep Performance Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <TopRepsTable
                data={topReps.data}
                isLoading={topReps.isLoading}
                error={topReps.error}
              />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <RepOrderTrendChart
                data={repOrderTrends.data}
                isLoading={repOrderTrends.isLoading}
                error={repOrderTrends.error}
              />
            </Paper>
          </Grid>

          {/* Product Intelligence Section */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={{ xs: 12, md: 6 }}>
                  <TopSkusTable
                    data={topSkus.data}
                    isLoading={topSkus.isLoading}
                    error={topSkus.error}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SkuCategoryChart
                    data={skuCategories.data}
                    isLoading={skuCategories.isLoading}
                    error={skuCategories.error}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <TrendingSkusChart
                  data={trendingSkus.data}
                  isLoading={trendingSkus.isLoading}
                  error={trendingSkus.error}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Order Composition Section */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <OrderCompositionCard
                data={orderComposition.data}
                isLoading={orderComposition.isLoading}
                error={orderComposition.error}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

export default OrderDashboard;
